import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'

import { triggerToast } from '@/components'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import {
  ERoleType,
  getRolesDisplayLabel,
  QUERY_KEY_USER_DETAIL,
  USER_LIST_QUERY_KEY,
} from '../constants'
import { useUpdateUser, useUpdateUserPermissions } from '../hooks'
import { useUserManagementStore } from '../store'

import { EditUserAccess } from './EditUserAccess'

export const EditUserDialog: React.FC = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const { editingUser, editDialogOpen, clearEditingUser, setEditDialogOpen } =
    useUserManagementStore()

  const [firstName, setFirstName] = React.useState<string>('')
  const [lastName, setLastName] = React.useState<string>('')
  // Track selected projects
  const [selectedProjectIds, setSelectedProjectIds] = React.useState<string[]>(
    []
  )

  // Store projects in a API
  const storedProjectIds = React.useRef<string[]>([])

  const { mutateAsync: updateUserAsync, isPending: isUpdatingUser } =
    useUpdateUser()
  const {
    mutateAsync: updateUserPermissionsAsync,
    isPending: isUpdatingUserPermissions,
  } = useUpdateUserPermissions()

  React.useEffect(() => {
    if (editingUser) {
      setFirstName(editingUser.first_name || '')
      setLastName(editingUser.last_name || '')
    }
  }, [editingUser])

  React.useEffect(() => {
    return () => {
      setSelectedProjectIds([])
    }
  }, [])

  const handleClose = React.useCallback(() => {
    setEditDialogOpen(false)
    clearEditingUser()
  }, [clearEditingUser, setEditDialogOpen])

  // Check if access is editable
  const accessEditableRole = React.useMemo(() => {
    return editingUser?.roles?.find((role) =>
      [ERoleType.PROJECT_LEAD, ERoleType.TEAM_MEMBER].includes(
        role.role_name as ERoleType
      )
    )
  }, [editingUser])

  const handleSave = React.useCallback(async () => {
    if (!editingUser || !firstName.trim() || !lastName.trim()) return

    const accessChanged =
      !!accessEditableRole &&
      JSON.stringify(selectedProjectIds) !==
        JSON.stringify(storedProjectIds.current)

    const promises: Promise<unknown>[] = [
      updateUserAsync({
        userId: editingUser.id,
        data: { first_name: firstName.trim(), last_name: lastName.trim() },
      }),
    ]

    // Update user access if access has changed
    if (accessChanged) {
      promises.push(
        updateUserPermissionsAsync({
          userId: editingUser.id,
          data: {
            role_id: accessEditableRole.role_id,
            scopes: selectedProjectIds.map((id) => ({
              resource_type: 'project',
              resource_id: id,
            })),
          },
        })
      )
    }

    try {
      await Promise.all(promises)
      triggerToast('success', t('userManagement.messages.updateSuccess'))
      queryClient.invalidateQueries({
        queryKey: USER_LIST_QUERY_KEY([]),
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_USER_DETAIL, editingUser.id],
      })
      handleClose()
    } catch {
      triggerToast('error', t('userManagement.messages.updateError'))
    }
  }, [
    accessEditableRole,
    editingUser,
    firstName,
    handleClose,
    lastName,
    queryClient,
    selectedProjectIds,
    t,
    updateUserAsync,
    updateUserPermissionsAsync,
  ])

  // Handle selected projects change
  const handleProjectsChange = React.useCallback(
    (projects: string[], isInitial?: boolean) => {
      setSelectedProjectIds(projects)
      // Store project ids for comparison
      if (isInitial) storedProjectIds.current = projects
    },
    []
  )

  // Check if save is disabled
  const isSaveDisabled: boolean = React.useMemo(() => {
    return (
      !firstName.trim() ||
      !lastName.trim() ||
      isUpdatingUser ||
      isUpdatingUserPermissions
    )
  }, [firstName, isUpdatingUser, isUpdatingUserPermissions, lastName])

  return (
    <Dialog open={editDialogOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[720px] p-0 !gap-2">
        <DialogHeader className="px-8">
          <DialogTitle className="text-lg font-semibold text-gray-900">
            {t('userManagement.modal.editTitle')}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 px-8 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">
                {t('userManagement.modal.firstName')}
              </Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder={t('userManagement.modal.firstNamePlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">
                {t('userManagement.modal.lastName')}
              </Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder={t('userManagement.modal.lastNamePlaceholder')}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="role">{t('userManagement.modal.role')}</Label>
            <Input
              id="role"
              value={getRolesDisplayLabel(editingUser?.roles)}
              disabled
              className="cursor-not-allowed bg-gray-50"
            />
          </div>
          {/* Edit user access */}
          {accessEditableRole && editingUser ? (
            <EditUserAccess
              user={editingUser}
              selectedProjectIds={selectedProjectIds}
              setSelectedProjectIds={handleProjectsChange}
            />
          ) : null}
        </div>

        <DialogFooter className="px-8 py-6">
          <Button variant="outlined" onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={isSaveDisabled}>
            {isUpdatingUser ? t('common.saving') : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
